import React, { useRef, useState } from 'react';
import { Paperclip, X, FileText, Loader2, Mic, Square } from 'lucide-react';
import { toast } from './ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const ATTACH_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,audio/mpeg,audio/wav,audio/ogg';
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;

export async function uploadAttachment(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/uploads`, { method: 'POST', body: fd, credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Upload failed');
  return { file_url: data.file_url, file_name: data.file_name, file_type: data.file_type, file_size: data.file_size };
}

export function AttachmentPicker({ attachments, setAttachments, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} attachments per message`);
      return;
    }
    setUploading(true);
    for (const f of files) {
      if (f.size > MAX_SIZE) { toast.error(`${f.name} is over 10MB`); continue; }
      try {
        const att = await uploadAttachment(f);
        setAttachments((prev) => [...prev, att]);
      } catch (err) {
        toast.error(`Could not upload ${f.name}`, { description: err.message });
      }
    }
    setUploading(false);
  };

  return (
    <>
      <input ref={inputRef} type="file" multiple accept={ATTACH_ACCEPT} className="hidden" onChange={onPick} data-testid="attachment-file-input" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 transition disabled:opacity-50 flex-shrink-0"
        title="Attach photos or documents"
        data-testid="attach-file-btn"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
      </button>
    </>
  );
}

export function AttachmentChips({ attachments, onRemove }) {
  if (!attachments?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="pending-attachments">
      {attachments.map((a, i) => (
        <span key={i} className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg pl-2 pr-1 py-0.5 text-[11px] font-medium max-w-[180px]">
          <span className="truncate">{a.file_name}</span>
          <button type="button" onClick={() => onRemove(i)} className="p-0.5 hover:bg-indigo-100 rounded" data-testid={`remove-attachment-${i}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

export function VoiceRecorderButton({ attachments, setAttachments, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stop = () => {
    try { recRef.current?.stop(); } catch { /* already stopped */ }
  };

  const start = async () => {
    if (attachments.length >= MAX_FILES) {
      toast.error(`Max ${MAX_FILES} attachments per message`);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder?.isTypeSupported?.('audio/webm') ? 'audio/webm'
        : window.MediaRecorder?.isTypeSupported?.('audio/mp4') ? 'audio/mp4' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
        const type = rec.mimeType?.split(';')[0] || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1000) { toast.error('Recording too short'); return; }
        const ext = type.includes('mp4') ? 'm4a' : 'webm';
        const file = new File([blob], `voice-note-${Date.now()}.${ext}`, { type });
        try {
          const att = await uploadAttachment(file);
          setAttachments((prev) => [...prev, att]);
          toast.success('Voice note attached — hit Send');
        } catch (err) {
          toast.error('Could not upload voice note', { description: err.message });
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => {
        if (s >= 119) stop();
        return s + 1;
      }), 1000);
    } catch (e) {
      toast.error('Microphone unavailable', { description: 'Allow microphone access to record voice notes' });
    }
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled}
      className={`p-2.5 rounded-xl border transition disabled:opacity-50 flex-shrink-0 inline-flex items-center gap-1 ${
        recording
          ? 'border-red-300 bg-red-50 text-red-600 animate-pulse'
          : 'border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300'
      }`}
      title={recording ? 'Stop recording' : 'Record a voice note'}
      data-testid="voice-note-btn"
    >
      {recording ? (
        <>
          <Square className="w-4 h-4 fill-current" />
          <span className="text-[11px] font-bold tabular-nums" data-testid="voice-note-timer">
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
          </span>
        </>
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </button>
  );
}

export function AttachmentList({ attachments, mine }) {
  if (!attachments?.length) return null;
  return (
    <div className="space-y-1.5 mt-1" data-testid="message-attachments">
      {attachments.map((a, i) => {
        const url = `${API_URL}${a.file_url}`;
        const type = a.file_type || '';
        if (type.startsWith('image/')) {
          return (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="block" data-testid="attachment-image">
              <img src={url} alt={a.file_name || 'Attachment'} className="max-w-[220px] max-h-44 rounded-lg border border-black/10 object-cover" loading="lazy" />
            </a>
          );
        }
        if (type.startsWith('audio/')) {
          return <audio key={i} controls src={url} className="max-w-[220px] h-9" data-testid="attachment-audio" />;
        }
        return (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium border transition ${
              mine ? 'bg-white/15 border-white/25 text-white hover:bg-white/25' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
            data-testid="attachment-file"
          >
            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate max-w-[170px]">{a.file_name || 'Document'}</span>
          </a>
        );
      })}
    </div>
  );
}
